import React from 'react';
import Portal from './../../common/Portal';
import PropTypes from 'prop-types';
import _ from 'lodash';
import CreateCollection from '../../queries/lightbox/crud/CreateCollection';
import LsCollections from '../../queries/lightbox/LsCollections';
import AddZmCollectionAssets from '../../queries/lightbox/assets/AddZmCollectionAssets';
import { FuidRrnInfo, LsZmCollectionsResponse, ZmCollection } from '../../autogenerated/ZoomBridge';
import ViewUtil from './../../ViewUtil';
import CollectionUtil from './../../queries/lightbox/CollectionUtil';
import Modal from '../Modal2';
import { toast } from '../../store/StoreUtil';
import t from '../../i18n/I18n';
import { ThemeSetup } from '../../themes/ThemeSetup';
import Store from '../../store/Store';
import { SELECTED_COLLECTION } from '../../store/Constants';
import ErrorHandler from '../../error/ErrorHandler';
import SearchUtil from '../../util/SearchUtil';

const debug = require('../../debug/debug')('wc:modals:AddToCollectionModal');

export default class AddToCollectionModal extends React.Component {
  static propTypes = {
    trigger: PropTypes.object,
    onClose: PropTypes.func.isRequired
  };

  state = {
    name: '',
    showHints: false,
    hints: [],
    focusedIndex: null,
    addToInRedColor: false
  };

  _allCollections = [];
  _allCollectionNames = [];

  componentDidMount() {
    this.refs.name.focus();
    this.fetchAllCollections();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.focusedIndex !== this.state.focusedIndex && this.state.hints.length) {
      const node = this._hintsNode.childNodes[this.state.focusedIndex];
      if (node && node.scrollIntoViewIfNeeded) {
        node.scrollIntoViewIfNeeded();
      }
    }
  }

  render() {
    const { onClose } = this.props;

    const modalProps = {
      onClose,
      name: 'AddToCollectionModal',
      title: t('ADD_TO_COLLECTION'),
      titleIcon: ThemeSetup.getImageURL('add_to_collection.svg'),
      buttons: [
        { label: t('CANCEL'), onClick: onClose, type: 'CANCEL' },
        { label: t('DONE_LABEL'), onClick: this.checkCollectionExistsOrNot, type: 'PRIMARY' }
      ]
    };

    const rect = this.refs.name ? this.refs.name.getBoundingClientRect() : {};
    const dropdownStyle = {
      top: rect.bottom || 0,
      left: rect.left || 0,
      width: rect.width || 0
    };

    const styles = getStyle();

    return (
      <Modal {...modalProps} closeOnEsc closeOnOutsideClick>
        <div style={styles.searchField}>
          <input
            ref="name"
            style={styles.input}
            value={this.state.name}
            onKeyDown={this.onKeyDown}
            onChange={this.onChange}
            onBlur={this.onBlurInput}
            type="text"
          />
          <div
            onClick={this.toggleHints}
            style={{
              ...styles.arrow,
              backgroundImage: _.isEmpty(this.state.hints) ? 'url(images/arrow_down.svg)' : 'url(images/arrow_up.svg)'
            }}
          />
          {!_.isEmpty(this.state.hints) ? (
            <Portal defaultOpen={!_.isEmpty(this.state.hints)} closeOnOutsideClick={true} closeOnEsc={true}>
              <div name="Dropdown_List" style={dropdownStyle} ref={(node) => (this._hintsNode = node)}>
                {_.map(this.state.hints, this.renderHint)}
              </div>
            </Portal>
          ) : null}
        </div>
      </Modal>
    );
  }

  renderHint = (hint, index) => {
    const styles = getStyle();
    let style = {
      ...styles.hint,
      backgroundColor: this.state.focusedIndex == index ? 'var(--dropdown-bg-selected)' : 'var(--primary-header-bg)'
    };
    let label = hint;
    let onClick = () => this.onSelect(hint);
    let allowFocus = true;

    return (
      <div
        key={hint}
        // onClick={onClick}
        onMouseDown={onClick}
        onMouseEnter={(event) => {
          if (allowFocus) this.setState({ focusedIndex: index });
        }}
        onMouseLeave={(event) => this.setState({ focusedIndex: null })}
        style={style}>
        {label}
      </div>
    );
  };

  push = () => {
    const selectedAsset = this.props.assets || window.gs.selectedAssets;
    debug('[push()] name: ', this.state.name);
    if (this.state.name.trim() == '') {
      debug('[push()] empty name. return.');
      return this.props.onClose();
    }

    const collection = _.find(this._allCollections, { _name: this.state.name });
    if (!collection) {
      debug('[push()] collection not found. return.');
      return toast(t('COLLECTION_DOES_NOT_EXIST'));
    }

    var assets = _.map(selectedAsset, (asset) =>
      new FuidRrnInfo()
        .setFuid(asset.getFuid())
        .setBid(asset.getBid())
        .setRrn(asset.getRrn())
    );

    const query = new AddZmCollectionAssets(collection.getCollectionId(), assets, null);
    debug('[push()] running query: ', query);
    query.run().then(
      () => {
        debug('[push()] navigating to collection: ', collection);
        Store.put(SELECTED_COLLECTION, collection)
        ViewUtil.switchToCollectionId(collection.getCollectionId());
      },
      (error) => {
        debug('[push()] error: ', error);
        toast(error.errorDetails);
      }
    );
  };

  create = () => {
    const selectedAsset = this.props.assets || window.gs.selectedAssets;
    debug('[create()] name: ', this.state.name);
    if (this.state.name.trim().length === 0) {
      debug('[create()] empty name. return.');
      toast(t('EMPTY_COLLECTION_NAME'));
      return;
    }

    let query = new CreateCollection(this.state.name.trim());

    _.each(selectedAsset, (asset) => {
      if (asset != null) {
        query.append(asset.getFuid(), asset.getBid(), asset.getRrn());
      }
    });

    debug('[create()] running query: ', query);
    // _.each(window.gs.selectedAssets, (asset) => query.append(asset.getFuid(), asset.getBid(), asset.getRrn()));
    query.run().then(
      (resp) => {
        debug('[create()] response: ', resp);
        ViewUtil.switchToCollectionId(resp);
      },
      (resp) => {
        debug('[create()] error: ', resp);
        ErrorHandler.handleError(resp.errorDetails);
        // toast(resp.errorDetails);
      }
    );
  };

  onChange = (event) => {
    const { value } = event.target;
    this.setState({ name: value }, () => {
      this.runAutocompleteQuery(value)
    }); 
  };

  getActiveCollections = (collections: ZmCollection[]) => {
    debug('[getActiveCollections()] collections: ', collections);
    const activeCollections = collections.filter((c) => c.getStatus() !== 'ARCHIVED');
    debug('[getActiveCollections()] active collections: ', activeCollections);
    return activeCollections;
  };

  runAutocompleteQuery = async (value) => {
    let suggestions = await SearchUtil.searchCollectionAutoComplete(value, false, true);
    let finalSuggestions = _.map(suggestions.getMatchedCollection(), '_name');
    this.setState({hints : finalSuggestions ?  finalSuggestions : []});
  };

  onKeyDown = (event) => {
    if (event.which == 40) {
      // arrow down
      if (this.state.focusedIndex == _.size(this.state.hints) - 1 || this.state.focusedIndex == null)
        this.setState({ focusedIndex: this.isValidName() ? 0 : 1 });
      else this.setState({ focusedIndex: this.state.focusedIndex + 1 });
      event.preventDefault();
    }

    if (event.which == 38) {
      // arrow up
      if (
        this.state.focusedIndex == 0 ||
        this.state.focusedIndex == null ||
        (this.state.focusedIndex == 1 && !this.isValidName())
      )
        this.setState({ focusedIndex: _.size(this.state.hints) - 1 });
      else this.setState({ focusedIndex: this.state.focusedIndex - 1 });
      event.preventDefault();
    }

    if (event.which == 27) {
      // ESC
      this.setState({ focusedIndex: null, hints: [] });
      event.preventDefault();
    }

    if (event.which == 13 && this.state.hints[this.state.focusedIndex]) {
      // ENTER
      this.setState({ name: this.state.hints[this.state.focusedIndex], hints: [], focusedIndex: null });
      event.preventDefault();
    }
    if (event.which == 13 && (_.isEmpty(this.state.hints) || this.state.focusedIndex == null)) {
      this.checkCollectionExistsOrNot();
    }
  };

  toggleHints = () => {
    if (_.isEmpty(this.state.hints)) {
      new LsCollections()
        .setPageLimit(1000)
        .setThumbImageCount(0)
        .setNameSearchString(this.state.name)
        .setOnlyCollections(true)
        .setOnlyBins(false)
        .setOwner(false)
        .run()
        .then(
          (resp) => {
            const activeCollections = this.getActiveCollections(resp.getCollections());
            const activeCollectionNames = _.map(activeCollections, '_name');
            const hints = _.uniq(this.state.hints.concat(activeCollectionNames));
            this.setState({ hints }, () => this.refs.name.focus());
          },
          (error) => this.setState({ focusedIndex: null }, () => this.refs.name.focus())
        );
    } else {
      this.setState({ hints: [], focusedIndex: null }, () => this.refs.name.focus());
    }
  };

  onBlurInput = (e) => {
    this.setState({ hints: [], focusedIndex: null });
  };

  closeWindow = (event) => {
    if (event.which == 27) {
      // ESC
      this.setState({ focusedIndex: null, hints: [] });
      this.props.onClose();
    }
  };

  checkCollectionExistsOrNot = () => {
    new LsCollections()
      .setPageLimit(1000)
      .setThumbImageCount(0)
      .setNameSearchString(this.state.name)
      .setOnlyBins(true)
      .setOnlyCollections(true)
      .setOwner(false)
      .run()
      .then(
        (resp) => {
          if (_.find(resp.getCollections(), { _name: this.state.name })) this.push();
          else this.create();
        },
        (error) => {
          this.create();
        }
      );
  };

  isValidCollectionName = (name) => {
    let slashes = /\\/;
    let squareBrac = /\[.*?\]/g;

    if (slashes.test(name) || /{/.test(name) || /}/.test(name) || /\[/g.test(name) || /\[/g.test(name)) return false;
    else return true;
  };

  isCollectionSelected = () => {
    const { name } = this.state;
    return _.includes(this._allCollectionNames, name.trim());
  };

  isValidName = () => {
    const { name } = this.state;
    return name.trim().length > 0 && !_.includes(this._allCollectionNames, name) && this.isValidCollectionName(name);
  };

  fetchAllCollections = async () => {
    const res = await new LsCollections()
      .setPageLimit(1000)
      .setThumbImageCount(0)
      .setNameSearchString('')
      .setOnlyCollections(true)
      .setOnlyBins(false)
      .setOwner(false)
      .run();
    this._allCollections = res.getCollections();
    this._allCollectionNames = this._allCollections.map((c) => c._name);
  };

  onSelect = (name) => {
    this.setState({ name, hints: [] }, () => this.refs.name.focus());
  };
}

function getStyle() {
  return {
    addToCollectionModal: {
      position: 'absolute',
      cursor: 'pointer',
      left: '0px',
      top: '0px',
      bottom: '0px',
      right: '0px',
      background: 'rgba(0, 0, 0, 0.2)',
      zIndex: '1',
      display: 'flex'
    },
    header: {
      position: 'relative',
      height: '30px'
    },
    icon: {
      position: 'absolute',
      background: 'var(--primary-header-bg)',
      borderRadius: '50%',
      height: 32,
      width: 32,
      backgroundImage: ThemeSetup.getBackgroundURL('add_to_collection.svg'),
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      backgroundSize: '30px'
    },
    text: {
      height: '24px',
      width: '261px',
      fontSize: '16px',
      color: '#3E3E3E',
      fontFamily: 'Montserrat',
      lineHeight: '24px',
      position: 'absolute',
      left: '40px',
      top: '6px'
    },
    content: {
      background: '#FAFAFA',
      width: '600px',
      margin: 'auto',
      borderRadius: '2px',
      boxShadow: '0px 5px 20px rgba(0, 0, 0, 0.15)'
    },
    footer: {
      borderTop: '1px solid #E7E7E7',
      position: 'relative',
      height: 40,
      padding: '0 15px'
    },
    done: {
      cursor: 'pointer',
      color: '#7A7A7A',
      fontSize: '12px',
      fontFamily: 'Montserrat',
      fontWeight: '600',
      letterSpacing: '1px',
      lineHeight: '15px'
    },
    add: {
      color: '#EB6A5C',
      fontFamily: 'Montserrat',
      fontSize: '12px',
      fontWeight: '600',
      letterSpacing: '1px',
      cursor: 'pointer'
    },
    input: {
      outline: 'none',
      width: '100%',
      border: 'none',
      fontSize: '16px',
      padding: '11px',
      borderTopLeftRadius: '2.5px',
      borderBottomLeftRadius: '2.5px'
    },
    body: {
      margin: '20px',
      position: 'relative',
      height: '41px'
    },
    searchField: {
      boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
      height: '41px',
      width: '100%',
      borderRadius: '2.5px',
      margin: '10px 0 30px',
      position: 'relative'
    },
    arrow: {
      width: '30px',
      position: 'absolute',
      right: 0,
      top: 0,
      height: '41px',
      backgroundColor: 'var(--primary-header-bg)',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      cursor: 'pointer',
      borderRightTopRadius: '2.5px',
      borderRightBottomRadius: '2.5px'
    },
    hints: {
      position: 'absolute',
      top: 42,
      left: 0,
      width: '100%',
      padding: '10px 0',
      background: 'var(--primary-header-bg)',
      zIndex: 160,
      maxHeight: 300,
      overflowY: 'scroll'
    },
    hint: {
      fontSize: '14px',
      color: 'var(--primary-font-color)',
      //fontWeight:600,
      padding: '5px 20px',
      cursor: 'pointer',
      fontFamily: 'Montserrat',
      lineHeight: '24px'
    },
    newCollection: {
      fontSize: '12px',
      fontWeight: 600,
      color: '#EB6A5C',
      padding: '5px 20px',
      cursor: 'pointer',
      fontFamily: 'Montserrat',
      lineHeight: '15px',
      letterSpacing: '1px',
      paddingBottom: '5px'
    },
    disabled: {
      color: '#7A7A7A',
      cursor: 'default',
      opacity: 0.8
    }
  };
}
